import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

const OBJECT_LOADING_EVENTS = ["model-loading", "image-loading", "pdf-loading", "video-loading"];
const OBJECT_LOADED_EVENTS = ["model-loaded", "image-loaded", "pdf-loaded", "video-loaded", "model-error"];
const ALL_LOADED_TIMEOUT = 3000;

const RoomAssetLoadingContext = createContext({
  objectCount: 0,
  loadedCount: 0,
  assetsLoading: true
});

export function RoomAssetLoadingProvider({ scene, children }) {
  const stateRef = useRef({
    objectCount: 0,
    loadedCount: 0,
    hasLogged: false,
    trackingActive: true,
    strategyKnown: false,
    loadingStrategy: null,
    awaitingNewEnvironment: !scene?.is?.("loaded"),
    loadingScreenDismissed: false,
    defaultStrategyTimeout: null
  });
  const allLoadedTimeoutRef = useRef(null);
  const [contextState, setContextState] = useState(() => ({
    objectCount: 0,
    loadedCount: 0,
    assetsLoading: !scene?.is?.("loaded")
  }));

  const updateContextState = useCallback(() => {
    // If loading screen is dismissed (e.g. implicit lazy), we report assetsLoading: false to UI
    // but we might still be tracking internally.
    const assetsStillLoading = stateRef.current.loadingScreenDismissed
      ? false
      : stateRef.current.strategyKnown
      ? stateRef.current.trackingActive
      : true;

    setContextState({
      objectCount: stateRef.current.objectCount,
      loadedCount: stateRef.current.loadedCount,
      assetsLoading: assetsStillLoading
    });
  }, []);

  useEffect(() => {
    if (!scene) return;
    // Completion events from detached entities no longer bubble to the scene.
    // Keep their outstanding loads so removal can settle them as cancellations.
    const pendingLoads = new Map();

    const parseStrategy = attr => {
      if (!attr) return "lazy";
      if (typeof attr === "string") {
        const match = attr.match(/strategy\s*:\s*(\w+)/i);
        return match ? match[1].toLowerCase() : attr.toLowerCase();
      }
      return attr.strategy || "lazy";
    };

    const applyLoadingStrategy = (strategy, isImplicit = false) => {
      const normalizedStrategy = strategy === "eager" ? "eager" : "lazy";
      const prevStrategy = stateRef.current.loadingStrategy;

      stateRef.current.loadingStrategy = normalizedStrategy;
      stateRef.current.strategyKnown = true;

      if (normalizedStrategy !== "eager") {
        // Lazy (Implicit or Explicit)
        if (isImplicit) {
          // Implicit Lazy: Dismiss screen, but keep tracking active to delay event
          stateRef.current.loadingScreenDismissed = true;
          stateRef.current.trackingActive = true; // Keep waiting for objects
        } else {
          // Explicit Lazy: Dismiss screen AND stop tracking (emit immediately if needed)
          stateRef.current.loadingScreenDismissed = true;
          if (stateRef.current.trackingActive || !stateRef.current.hasLogged) {
            stateRef.current.trackingActive = false;
            if (!stateRef.current.hasLogged) {
              stateRef.current.hasLogged = true;
              scene.emit("all-objects-loaded");
            }
          }
        }
        updateContextState();
        return;
      }

      // Eager
      if (prevStrategy !== "eager") {
        // Switching to Eager.
        // If we were implicit lazy, screen is already dismissed. We don't bring it back.
        // But we ensure tracking is active.
      }

      if (!stateRef.current.hasLogged) {
        stateRef.current.trackingActive = true;
        updateContextState();
      }
    };

    const evaluateLoadingStrategy = () => {
      if (stateRef.current.awaitingNewEnvironment) {
        return false;
      }
      const strategyAttr = document.querySelector("[loading-strategy]")?.getAttribute("loading-strategy");
      if (!strategyAttr) {
        // Check if we already have a pending default timeout
        if (stateRef.current.defaultStrategyTimeout) return true;

        stateRef.current.defaultStrategyTimeout = setTimeout(() => {
          // Re-check
          const currentAttr = document.querySelector("[loading-strategy]")?.getAttribute("loading-strategy");
          if (!currentAttr) {
            applyLoadingStrategy("lazy", true);
          }
          stateRef.current.defaultStrategyTimeout = null;
        }, 1000);
        return true;
      }

      // If we found one, clear any pending default
      if (stateRef.current.defaultStrategyTimeout) {
        clearTimeout(stateRef.current.defaultStrategyTimeout);
        stateRef.current.defaultStrategyTimeout = null;
      }

      applyLoadingStrategy(parseStrategy(strategyAttr), false);
      return true;
    };

    const resetState = ({ resetStrategy } = { resetStrategy: false }) => {
      pendingLoads.clear();
      stateRef.current = {
        objectCount: 0,
        loadedCount: 0,
        hasLogged: false,
        trackingActive: resetStrategy ? true : stateRef.current.trackingActive,
        strategyKnown: resetStrategy ? false : stateRef.current.strategyKnown,
        loadingStrategy: resetStrategy ? null : stateRef.current.loadingStrategy,
        awaitingNewEnvironment: resetStrategy ? true : stateRef.current.awaitingNewEnvironment,
        loadingScreenDismissed: resetStrategy ? false : stateRef.current.loadingScreenDismissed,
        defaultStrategyTimeout: resetStrategy ? null : stateRef.current.defaultStrategyTimeout
      };
      if (allLoadedTimeoutRef.current) {
        clearTimeout(allLoadedTimeoutRef.current);
        allLoadedTimeoutRef.current = null;
      }
      if (resetStrategy && stateRef.current.defaultStrategyTimeout) {
        clearTimeout(stateRef.current.defaultStrategyTimeout);
        stateRef.current.defaultStrategyTimeout = null;
      }
      if (resetStrategy) {
        updateContextState();
      } else {
        updateContextState();
      }
    };

    const scheduleAllLoadedCheck = () => {
      const { objectCount, loadedCount, hasLogged, trackingActive } = stateRef.current;
      // Cancelling temporary UI must not finish a room before its environment
      // has inflated and announced all of the destination's asset loads.
      if (stateRef.current.awaitingNewEnvironment) return;
      if (!trackingActive || hasLogged || objectCount === 0 || loadedCount < objectCount) {
        return;
      }

      if (allLoadedTimeoutRef.current) {
        return;
      }

      allLoadedTimeoutRef.current = setTimeout(() => {
        allLoadedTimeoutRef.current = null;
        if (stateRef.current.awaitingNewEnvironment) return;
        const {
          objectCount: updatedCount,
          loadedCount: updatedLoaded,
          hasLogged: updatedLogged,
          trackingActive: updatedTrackingActive
        } = stateRef.current;
        if (!updatedLogged && updatedTrackingActive && updatedCount > 0 && updatedLoaded >= updatedCount) {
          stateRef.current.hasLogged = true;
          stateRef.current.trackingActive = false;
          scene.emit("all-objects-loaded");
          updateContextState();
        }
      }, ALL_LOADED_TIMEOUT);
    };

    const handleObjectLoading = event => {
      if (!stateRef.current.trackingActive) {
        return;
      }
      const type = event.type.split("-")[0];
      const loads = pendingLoads.get(event.target) || new Map();
      loads.set(type, (loads.get(type) || 0) + 1);
      pendingLoads.set(event.target, loads);
      stateRef.current.objectCount += 1;
      stateRef.current.hasLogged = false;
      if (allLoadedTimeoutRef.current) {
        clearTimeout(allLoadedTimeoutRef.current);
        allLoadedTimeoutRef.current = null;
      }
      updateContextState();
    };

    const handleObjectLoaded = event => {
      if (!stateRef.current.trackingActive) {
        return;
      }
      const type = event.type.split("-")[0];
      const loads = pendingLoads.get(event.target);
      const count = loads?.get(type);
      // Ignore duplicate/late completions and loads started before this tracker.
      if (!count) return;
      if (count === 1) loads.delete(type);
      else loads.set(type, count - 1);
      if (loads.size === 0) pendingLoads.delete(event.target);
      stateRef.current.loadedCount += 1;
      scheduleAllLoadedCheck();
      updateContextState();
    };

    const settleRemovedObjects = () => {
      if (!stateRef.current.trackingActive) return;
      let cancelled = 0;
      for (const [entity, loads] of pendingLoads) {
        // A move within this scene is not a cancellation. Check after the DOM
        // mutation batch, including removals of ancestors of pending entities.
        if (scene.contains(entity)) continue;
        for (const count of loads.values()) cancelled += count;
        pendingLoads.delete(entity);
      }
      if (!cancelled) return;
      stateRef.current.loadedCount += cancelled;
      scheduleAllLoadedCheck();
      updateContextState();
    };

    const handleEnvironmentLoaded = () => {
      stateRef.current.awaitingNewEnvironment = false;
      evaluateLoadingStrategy();
      scheduleAllLoadedCheck();
    };

    const handleSceneReset = () => {
      resetState({ resetStrategy: true });
    };

    const handleHubChangeStarted = () => {
      resetState({ resetStrategy: true });
    };

    OBJECT_LOADING_EVENTS.forEach(eventName => {
      scene.addEventListener(eventName, handleObjectLoading);
    });

    OBJECT_LOADED_EVENTS.forEach(eventName => {
      scene.addEventListener(eventName, handleObjectLoaded);
    });

    scene.addEventListener("reset_scene", handleSceneReset);
    scene.addEventListener("hub-change-started", handleHubChangeStarted);
    scene.addEventListener("environment-scene-loaded", handleEnvironmentLoaded);
    evaluateLoadingStrategy();

    // Watch for changes to loading-strategy attribute
    const observer = new MutationObserver(mutations => {
      if (mutations.some(mutation => mutation.type === "childList" && mutation.removedNodes.length > 0)) {
        settleRemovedObjects();
      }
      for (const mutation of mutations) {
        if (mutation.type === "attributes" && mutation.attributeName === "loading-strategy") {
          evaluateLoadingStrategy();
        } else if (mutation.type === "childList") {
          // Check if any added nodes have the attribute
          const hasStrategy = Array.from(mutation.addedNodes).some(
            node =>
              node.nodeType === 1 && (node.hasAttribute("loading-strategy") || node.querySelector("[loading-strategy]"))
          );
          if (hasStrategy) {
            evaluateLoadingStrategy();
          }
        }
      }
    });

    observer.observe(scene, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ["loading-strategy"]
    });

    return () => {
      observer.disconnect();
      pendingLoads.clear();
      OBJECT_LOADING_EVENTS.forEach(eventName => {
        scene.removeEventListener(eventName, handleObjectLoading);
      });
      OBJECT_LOADED_EVENTS.forEach(eventName => {
        scene.removeEventListener(eventName, handleObjectLoaded);
      });
      scene.removeEventListener("reset_scene", handleSceneReset);
      scene.removeEventListener("hub-change-started", handleHubChangeStarted);
      scene.removeEventListener("environment-scene-loaded", handleEnvironmentLoaded);
      if (allLoadedTimeoutRef.current) {
        clearTimeout(allLoadedTimeoutRef.current);
        allLoadedTimeoutRef.current = null;
      }
    };
  }, [scene, updateContextState]);

  return <RoomAssetLoadingContext.Provider value={contextState}>{children}</RoomAssetLoadingContext.Provider>;
}

RoomAssetLoadingProvider.propTypes = {
  scene: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired
};

export function useRoomAssetLoading() {
  return useContext(RoomAssetLoadingContext);
}
